import React, { useMemo } from 'react';
import { Tabs, Table, Tag, Space, Button, Empty } from 'antd';
import { FileExcelOutlined, DownloadOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import * as XLSX from 'xlsx';
import { emissionsColumns, sinistresColumns } from '../RetrocessionData';
import { toast } from 'react-toastify';
import { displayNumbers } from '../../../../variables/numbersFunctions';

const BordereauxDetailles = ({ selectedGroup }) => {

    const telechargerBordereau = (data, nomFichier, compagnies, type = 'emissions') => {
        if (!data || data.length === 0) {
            toast.warning('Aucune donnée à télécharger');
            return;
        }

        try {
            const parsedData = typeof data === 'string' ? JSON.parse(data) : data;
            if (parsedData.length === 0) {
                toast.warning('Aucune donnée à télécharger');
                return;
            }

            const isEmissionType = ['emissions', 'emissions-reportees', 'annulations'].includes(type);

            // --- Construction des en-têtes ---
            const baseHeaders = isEmissionType
                ? ['N° Police', 'Compagnie cédante', 'Effet', 'Échéance', 'Prime cédée']
                : ['N° Sinistre', 'N° Police', 'Souscripteur', 'Date de survenance', 'Bénéficiaire', 'Montant'];

            const headerRow1 = [...baseHeaders];
            const headerRow2 = [...baseHeaders.map(() => '')]; // vide sous les colonnes de base

            compagnies.forEach((c) => {
                headerRow1.push(c.raisonSociale, ''); // merge sur 2 colonnes
                headerRow2.push('Taux', 'Montant');
            });

            headerRow1.push('Total Rétrocédé');
            headerRow2.push('');

            // --- Construction des lignes de données ---
            const rows = parsedData.map((row) => {
                const baseValues = isEmissionType
                    ? [
                        row.numeroPolice || 'N/A',
                        row.compagnie?.raisonSociale || '',
                        row.effet && row.effet !== 'N/A' ? dayjs(row.effet).format('DD/MM/YYYY') : 'N/A',
                        row.echeance && row.echeance !== 'N/A' ? dayjs(row.echeance).format('DD/MM/YYYY') : 'N/A',
                        row.primeACeder || 0,
                    ]
                    : [
                        row.sinistreNumSinistre || 'N/A',
                        row.sinistreNumPolice || 'N/A',
                        row.sinistreSouscripteur || '',
                        row.dateSinistre && row.dateSinistre !== 'N/A' ? dayjs(row.dateSinistre).format('DD/MM/YYYY') : 'N/A',
                        row.beneficiaire || '',
                        row.montant || 0,
                    ];

                const compagnieValues = [];
                compagnies.forEach((c) => {
                    const rep = row.repartition?.find((r) => r.compagnieCode === c.code);
                    compagnieValues.push(rep ? rep.taux : 0);
                    compagnieValues.push(rep ? rep.montant : 0);
                });

                return [...baseValues, ...compagnieValues, row.totalRetrocede || 0];
            });

            // --- Ligne de totaux ---
            const nbBase = baseHeaders.length;
            const totalsRow = new Array(nbBase).fill('');
            totalsRow[0] = 'TOTAUX';

            // Total de la colonne principale (prime ou montant sinistre)
            const principalColIdx = isEmissionType ? 4 : 5;
            totalsRow[principalColIdx] = parsedData.reduce(
                (sum, r) => sum + (isEmissionType ? (r.primeACeder || 0) : (r.montant || 0)),
                0
            );

            compagnies.forEach((c) => {
                totalsRow.push(''); // taux : pas de total
                totalsRow.push(
                    parsedData.reduce((sum, r) => {
                        const rep = r.repartition?.find((rr) => rr.compagnieCode === c.code);
                        return sum + (rep?.montant || 0);
                    }, 0)
                );
            });

            totalsRow.push(parsedData.reduce((sum, r) => sum + (r.totalRetrocede || 0), 0));

            // --- Assemblage du worksheet ---
            const wsData = [headerRow1, headerRow2, ...rows, totalsRow];
            const ws = XLSX.utils.aoa_to_sheet(wsData);

            // --- Merges des en-têtes ---
            const merges = [];

            // Merge vertical des colonnes de base (ligne 0 et 1)
            baseHeaders.forEach((_, i) => {
                merges.push({ s: { r: 0, c: i }, e: { r: 1, c: i } });
            });

            // Merge horizontal des noms de compagnies (2 colonnes chacune)
            let colOffset = nbBase;
            compagnies.forEach(() => {
                merges.push({ s: { r: 0, c: colOffset }, e: { r: 0, c: colOffset + 1 } });
                colOffset += 2;
            });

            // Merge vertical "Total Rétrocédé"
            merges.push({ s: { r: 0, c: colOffset }, e: { r: 1, c: colOffset } });

            ws['!merges'] = merges;

            // --- Largeurs de colonnes ---
            const colWidths = [];
            baseHeaders.forEach((h) => {
                colWidths.push({ wch: Math.max(h.length + 2, 14) });
            });
            compagnies.forEach(() => {
                colWidths.push({ wch: 10 }); // Taux
                colWidths.push({ wch: 18 }); // Montant
            });
            colWidths.push({ wch: 18 }); // Total Rétrocédé
            ws['!cols'] = colWidths;

            // --- Formatage numérique ---
            const totalCols = nbBase + compagnies.length * 2 + 1;
            const totalRows = wsData.length;

            for (let R = 2; R < totalRows; R++) {
                // Colonne principale (prime/montant)
                const principalCell = XLSX.utils.encode_cell({ r: R, c: principalColIdx });
                if (ws[principalCell] && typeof ws[principalCell].v === 'number') {
                    ws[principalCell].z = '#,##0';
                }

                // Colonnes compagnies
                for (let ci = 0; ci < compagnies.length; ci++) {
                    const tauxCol = nbBase + ci * 2;
                    const montantCol = nbBase + ci * 2 + 1;

                    const tauxCell = XLSX.utils.encode_cell({ r: R, c: tauxCol });
                    if (ws[tauxCell] && typeof ws[tauxCell].v === 'number') {
                        ws[tauxCell].z = '0.00"%"';
                    }

                    const montantCell = XLSX.utils.encode_cell({ r: R, c: montantCol });
                    if (ws[montantCell] && typeof ws[montantCell].v === 'number') {
                        ws[montantCell].z = '#,##0';
                    }
                }

                // Total rétrocédé
                const totalCell = XLSX.utils.encode_cell({ r: R, c: totalCols - 1 });
                if (ws[totalCell] && typeof ws[totalCell].v === 'number') {
                    ws[totalCell].z = '#,##0';
                }
            }

            const wb = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(wb, ws, 'Bordereau');
            XLSX.writeFile(wb, `${nomFichier}.xlsx`);

            toast.success('Bordereau téléchargé avec succès');
        } catch (error) {
            console.error(error);
            toast.error('Erreur lors du téléchargement du bordereau');
        }
    };
    const retrocessions = useMemo(
        () => Object.values(selectedGroup.retrocessions ?? {}),
        [selectedGroup]
    );

    // Liste des compagnies
    const compagnies = useMemo(() => {
        return retrocessions.map((r) => ({
            code: r.compagnie.code,
            raisonSociale: r.compagnie.raisonSociale,
        }));
    }, [retrocessions]);

    const parseData = (dataString) => {
        if (!dataString) return [];
        try {
            return typeof dataString === 'string' ? JSON.parse(dataString) : dataString;
        } catch {
            return [];
        }
    };

    /**
     * Fusionne les données de toutes les rétrocessions en un seul tableau.
     * Regroupe par clé unique (codeId pour émissions, sinistreNumSinistre pour sinistres)
     * et assemble toutes les répartitions dans un seul tableau.
     */
    const mergeData = (fieldName, keyExtractor) => {
        const merged = new Map();
        // console.log("Retrocessions pour fusion:", retrocessions);
        retrocessions.forEach((retro) => {
            const items = parseData(retro[fieldName]);
            const compagnieCode = retro.compagnie.code;
            const compagnieNom = retro.compagnie.raisonSociale;

            items.forEach((item) => {
                const key = keyExtractor(item);
                if (!key) return;

                if (!merged.has(key)) {
                    // Première occurrence : on copie la ligne de base sans répartition
                    merged.set(key, {
                        ...item,
                        repartition: [],
                        totalRetrocede: 0,
                    });
                }

                const existing = merged.get(key);

                // Ajouter la répartition de cette compagnie
                // Soit depuis item.repartition, soit depuis les champs legacy
                const itemRepartitions = item.repartition || [];
                const repForThisCompagnie = itemRepartitions.find(
                    (r) => r.compagnieCode === compagnieCode
                );

                if (repForThisCompagnie) {
                    // Vérifier qu'on n'a pas déjà cette compagnie
                    const alreadyExists = existing.repartition.some(
                        (r) => r.compagnieCode === compagnieCode
                    );
                    if (!alreadyExists) {
                        existing.repartition.push({
                            compagnieCode: compagnieCode,
                            compagnieNom: compagnieNom,
                            taux: repForThisCompagnie.taux || 0,
                            montant: repForThisCompagnie.montant || 0,
                        });
                        existing.totalRetrocede += repForThisCompagnie.montant || 0;
                    }
                } else if (item.tauxRetrocede != null || item.montantRetrocede != null) {
                    // Fallback champs legacy
                    const alreadyExists = existing.repartition.some(
                        (r) => r.compagnieCode === compagnieCode
                    );
                    if (!alreadyExists) {
                        existing.repartition.push({
                            compagnieCode: compagnieCode,
                            compagnieNom: compagnieNom,
                            taux: item.tauxRetrocede || 0,
                            montant: item.montantRetrocede || 0,
                        });
                        existing.totalRetrocede += item.montantRetrocede || 0;
                    }
                }
            });
        });

        return Array.from(merged.values());
    };

    // Clés d'extraction selon le type
    const emissionKeyExtractor = (item) => item.codeId || `${item.numeroPolice}_${item.avenant}`;
    const sinistreKeyExtractor = (item) => item.codeId || item.sinistreNumSinistre;

    const moisFormatted = useMemo(() => {
        if (retrocessions.length === 0) return '';
        return dayjs(retrocessions[0].moisRetrocession).format('MM-YYYY');
    }, [retrocessions]);

    // Données fusionnées
    const mergedEmissions = useMemo(
        () => mergeData('emissionsData', emissionKeyExtractor),
        [retrocessions]
    );
    // console.log('Merged Emissions:', mergedEmissions);
    const mergedEmissionsReportees = useMemo(
        // Une ligne SANS date d'effet = anomalie (contrat sans date), PAS une vraie émission reportée :
        // on l'exclut de la section (elle n'appartient à aucun exercice futur).
        () => mergeData('emissionsReporteesData', emissionKeyExtractor)
            .filter((row) => row?.effet && row.effet !== 'N/A'),
        [retrocessions]
    );
    // console.log('Merged Emissions Reportées:', mergedEmissionsReportees);
    const mergedAnnulations = useMemo(
        () => mergeData('annulationsData', emissionKeyExtractor),
        [retrocessions]
    );
    const mergedSinistres = useMemo(
        () => mergeData('sinistresData', sinistreKeyExtractor),
        [retrocessions]
    );
    const mergedRecours = useMemo(
        () => mergeData('recoursData', sinistreKeyExtractor),
        [retrocessions]
    );

    const bordereaux = [
        {
            key: 'emissions',
            titre: 'Émissions',
            data: mergedEmissions,
            nom: `Emissions_Toutes_Compagnies_${moisFormatted}`,
            columns: emissionsColumns(compagnies, true),
            color: '#52c41a',
        },
        {
            key: 'emissions-reportees',
            titre: 'Émissions Reportées',
            data: mergedEmissionsReportees,
            nom: `Emissions_Reportees_Toutes_Compagnies_${moisFormatted}`,
            columns: emissionsColumns(compagnies, true),
            color: '#1890ff',
        },
        {
            key: 'annulations',
            titre: 'Annulations',
            data: mergedAnnulations,
            nom: `Annulations_Toutes_Compagnies_${moisFormatted}`,
            columns: emissionsColumns(compagnies, true),
            color: '#faad14',
        },
        {
            key: 'sinistres',
            titre: 'Sinistres',
            data: mergedSinistres,
            nom: `Sinistres_Toutes_Compagnies_${moisFormatted}`,
            columns: sinistresColumns(compagnies),
            color: '#ff4d4f',
        },
        {
            key: 'recours',
            titre: 'Recours',
            data: mergedRecours,
            nom: `Recours_Toutes_Compagnies_${moisFormatted}`,
            columns: sinistresColumns(compagnies),
            color: '#722ed1',
        },
    ];

    if (retrocessions.length === 0) {
        return <Empty description="Aucune donnée de rétrocession disponible" />;
    }

    return (
        <Tabs
            type="line"
            tabBarStyle={{ marginBottom: 16 }}
            items={bordereaux.map((bordereau) => ({
                key: bordereau.key,
                label: (
                    <Space>
                        <span style={{ color: bordereau.color }}>●</span>
                        {bordereau.titre}
                        <Tag color={bordereau.color}>{bordereau.data.length}</Tag>
                    </Space>
                ),
                children: (
                    <div>
                        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'flex-end' }}>
                            <Button
                                type="primary"
                                icon={<DownloadOutlined />}
                                onClick={() =>
                                    telechargerBordereau(
                                        bordereau.data,
                                        bordereau.nom,
                                        compagnies,
                                        bordereau.key
                                    )
                                }
                                disabled={bordereau.data.length === 0}
                                style={{
                                    background: bordereau.color,
                                    borderColor: bordereau.color,
                                }}
                            >
                                Télécharger Excel ({bordereau.data.length} lignes)
                            </Button>
                        </div>

                        {bordereau.data.length > 0 ? (
                            <Table
                                columns={bordereau.columns}
                                dataSource={bordereau.data.map((item, i) => ({
                                    ...item,
                                    key: item.codeId || item.sinistreNumSinistre || i,
                                }))}
                                size="small"
                                bordered
                                scroll={{ x: 'max-content', y: 500 }}
                                pagination={{
                                    pageSize: 50,
                                    showSizeChanger: true,
                                    pageSizeOptions: ['20', '50', '100', '200'],
                                    showTotal: (total) => `Total: ${total} lignes`,
                                }}
                                summary={() => {
                                    const isEmissionType = ['emissions', 'emissions-reportees', 'annulations'].includes(bordereau.key);
                                    const allData = bordereau.data; // totalité des données, pas juste la page

                                    const totalRetrocede = allData.reduce(
                                        (sum, row) => sum + (row.totalRetrocede || 0),
                                        0
                                    );

                                    return (
                                        <Table.Summary fixed>
                                            <Table.Summary.Row style={{ background: '#fafafa', fontWeight: 'bold' }}>
                                                <Table.Summary.Cell
                                                    index={0}
                                                    colSpan={isEmissionType ? 5 : 6}
                                                    align="right"
                                                >
                                                    TOTAUX
                                                </Table.Summary.Cell>
                                                {compagnies.map((c, idx) => (
                                                    <React.Fragment key={idx}>
                                                        <Table.Summary.Cell align="right">—</Table.Summary.Cell>
                                                        <Table.Summary.Cell align="right">
                                                            {displayNumbers(
                                                                allData.reduce((sum, row) => {
                                                                    const rep = row.repartition?.find(
                                                                        (r) => r.compagnieCode === c.code
                                                                    );
                                                                    return sum + (rep?.montant || 0);
                                                                }, 0)
                                                            )}{' '}F
                                                        </Table.Summary.Cell>
                                                    </React.Fragment>
                                                ))}
                                                <Table.Summary.Cell align="right">
                                                    <strong style={{ color: '#722ed1', fontSize: 14 }}>
                                                        {displayNumbers(totalRetrocede)} F
                                                    </strong>
                                                </Table.Summary.Cell>
                                            </Table.Summary.Row>
                                        </Table.Summary>
                                    );
                                }}
                            />
                        ) : (
                            <Empty description={`Aucune donnée de ${bordereau.titre.toLowerCase()}`} />
                        )}
                    </div>
                ),
            }))}
        />
    );
};

export default BordereauxDetailles;